/**
 * Snapshot lógico — fallback quando readback de <video> falha (Tizen HW overlay).
 *
 * Busca miniatura JPEG autenticada no backend (poster existente ou ffmpeg efêmero),
 * cacheia por contentId no player, compõe mini centralizada no canvas (legenda no admin).
 */
import { deviceTokenService } from '~/services/auth/DeviceToken';
import { streamStore } from '~/stores';
import { resolveApiBaseUrl } from '~/services/embedded/hostConfig';

export interface PlaybackContext {
  mode: 'logical_fallback';
  contentId: string;
  contentName: string;
  positionSec: number;
  durationSec: number;
}

const FRAME_FETCH_TIMEOUT_MS = 20_000;
/** Fração máxima da área ocupada pela mini (object-fit contain, centralizada). */
const THUMB_MAX_AREA_FRACTION = 0.55;

interface CachedFrame {
  objectUrl: string;
  naturalWidth: number;
  naturalHeight: number;
}

const frameCache = new Map<string, CachedFrame>();

/** Limpa cache — só para testes. */
export function clearLogicalFrameCache(): void {
  for (const entry of frameCache.values()) {
    URL.revokeObjectURL(entry.objectUrl);
  }
  frameCache.clear();
}

function getCaptureApiBaseUrl(): string {
  try {
    return resolveApiBaseUrl();
  } catch {
    return 'http://localhost:8082';
  }
}

export function resolveVideoContentId(video: HTMLVideoElement): string | null {
  const fromData = video.dataset.contentId?.trim();
  return fromData || null;
}

/**
 * Este `<video>` está REALMENTE na tela agora?
 *
 * <p><b>Por que a captura precisa perguntar isso.</b> Desde o motor agendado A/B há
 * DOIS elementos de vídeo no DOM: o que está tocando e o standby, que carrega o
 * próximo em silêncio — decodificado, <b>pausado</b> e com {@code visibility:hidden}.
 * A captura varria todos e desenhava cada um na mesma área; quando o standby era
 * desenhado por último, seu placeholder opaco ("⏸ Vídeo pausado") cobria o frame real.
 * Era por isso que a primeira captura vinha pausada e "Nova Captura" vinha certa: no
 * meio-tempo os papéis trocavam e a ordem invertia.</p>
 *
 * <p>Também cobre o vídeo ativo escondido durante o freeze de transição
 * ({@code hideVideoForFreeze}, Tizen/webOS): ali quem está na tela é o canvas de
 * congelamento, e desenhar o vídeo por cima apagaria justamente o que o operador vê.</p>
 *
 * <p>Critério = o que a tela mostra, não qual elemento é o "certo": área > 0 e nem
 * {@code visibility:hidden} nem {@code display:none}. Sem acesso a
 * {@code getComputedStyle} (jsdom parcial, WebView exótica), assume VISÍVEL — perder
 * um frame real é pior que desenhar um placeholder a mais.</p>
 */
export function isVideoOnScreen(video: HTMLVideoElement): boolean {
  const rect = video.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  try {
    const style = window.getComputedStyle(video);
    if (style.visibility === 'hidden' || style.display === 'none') return false;
  } catch {
    return true;
  }
  return true;
}

function getDeviceCredentials(): {
  deviceId: string;
  deviceToken: string;
} | null {
  const deviceId = localStorage.getItem('efflux_device_id')?.trim();
  if (!deviceId) return null;
  const deviceToken = deviceTokenService.getTokenOrNull();
  if (!deviceToken) return null;
  return { deviceId, deviceToken };
}

function buildFrameUrl(
  deviceId: string,
  contentId: string,
  atSec: number,
): string {
  const base = getCaptureApiBaseUrl().replace(/\/$/, '');
  const params = new URLSearchParams({ at: String(Math.max(0, atSec)) });
  return `${base}/api/v1/remote-player/devices/${encodeURIComponent(deviceId)}/conteudos/${encodeURIComponent(contentId)}/frame?${params}`;
}

/**
 * Posição e duração do item atual — mesma regra do PlaybackStatusService.
 */
export function resolvePlaybackContext(
  contentId: string,
  video: HTMLVideoElement,
): PlaybackContext {
  const media = streamStore.currentMedia;
  const contentName =
    media?.id === contentId ? media.name : media?.name?.trim() || 'Vídeo';

  const durationSec = Math.max(
    0,
    Math.floor(
      media?.id === contentId && media.duration != null && media.duration > 0
        ? media.duration
        : Number.isFinite(video.duration) && video.duration > 0
          ? video.duration
          : (media?.duration ?? 0),
    ),
  );

  let positionSec = 0;
  const startedAt = streamStore.currentItemStartedAt;
  if (startedAt && Number.isFinite(startedAt)) {
    positionSec = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  } else if (!Number.isNaN(video.currentTime)) {
    positionSec = Math.max(0, Math.floor(video.currentTime));
  }

  return {
    mode: 'logical_fallback',
    contentId,
    contentName,
    positionSec,
    durationSec,
  };
}

function loadImageFromUrl(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    let settled = false;

    const finish = (result: HTMLImageElement | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    };

    const timer = setTimeout(() => finish(null), FRAME_FETCH_TIMEOUT_MS);

    img.onload = () => {
      if (img.naturalWidth <= 0 || img.naturalHeight <= 0) {
        finish(null);
        return;
      }
      finish(img);
    };
    img.onerror = () => finish(null);
    img.src = url;
  });
}

async function fetchFrameBlob(
  frameUrl: string,
  deviceToken: string,
): Promise<Blob | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FRAME_FETCH_TIMEOUT_MS);

  try {
    const res = await fetch(frameUrl, {
      method: 'GET',
      headers: { 'X-Device-Token': deviceToken },
      signal: controller.signal,
    });
    if (!res.ok) {
      console.warn(
        `[captureLogicalFallback] frame fetch HTTP ${res.status} — ${frameUrl}`,
      );
      // Drena antes de sair: o caminho de sucesso consome via `blob()`, mas o de
      // erro largava a `Response` sem ler nem cancelar — o corpo tem bytes de
      // verdade e isso segura o data pipe do Chromium (~2 MB de address space
      // por requisição no renderer 32-bit; causa-raiz do renderer-kill do
      // Tab A7, commit 683464d).
      await res.body?.cancel().catch(() => undefined);
      return null;
    }
    const blob = await res.blob();
    if (!blob.size || !blob.type.startsWith('image/')) {
      return null;
    }
    return blob;
  } catch (err) {
    console.warn('[captureLogicalFallback] frame fetch falhou:', err);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

async function getOrFetchFrameImage(
  contentId: string,
  atSec: number,
): Promise<{
  img: HTMLImageElement;
  frameUrl: string;
  fromCache: boolean;
} | null> {
  const cached = frameCache.get(contentId);
  if (cached) {
    const img = await loadImageFromUrl(cached.objectUrl);
    if (img) {
      return {
        img,
        frameUrl: `(cache:${contentId})`,
        fromCache: true,
      };
    }
    URL.revokeObjectURL(cached.objectUrl);
    frameCache.delete(contentId);
  }

  const creds = getDeviceCredentials();
  if (!creds) {
    console.warn(
      '[captureLogicalFallback] deviceId/token ausentes — skip frame fetch',
    );
    return null;
  }

  const frameUrl = buildFrameUrl(creds.deviceId, contentId, atSec);
  const blob = await fetchFrameBlob(frameUrl, creds.deviceToken);
  if (!blob) return null;

  const objectUrl = URL.createObjectURL(blob);
  const img = await loadImageFromUrl(objectUrl);
  if (!img) {
    URL.revokeObjectURL(objectUrl);
    return null;
  }

  frameCache.set(contentId, {
    objectUrl,
    naturalWidth: img.naturalWidth,
    naturalHeight: img.naturalHeight,
  });

  return { img, frameUrl, fromCache: false };
}

function drawCenteredThumbnailContain(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  destX: number,
  destY: number,
  destW: number,
  destH: number,
): void {
  const srcW = img.naturalWidth;
  const srcH = img.naturalHeight;
  if (srcW <= 0 || srcH <= 0) return;

  const maxW = destW * THUMB_MAX_AREA_FRACTION;
  const maxH = destH * THUMB_MAX_AREA_FRACTION;
  const scale = Math.min(1, maxW / srcW, maxH / srcH);
  const drawW = srcW * scale;
  const drawH = srcH * scale;
  const x = destX + (destW - drawW) / 2;
  const y = destY + (destH - drawH) / 2;

  ctx.drawImage(img, x, y, drawW, drawH);
}

function truncateLabel(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return `${text.slice(0, Math.max(0, maxLen - 1))}…`;
}

export function drawLogicalSnapshotLegend(
  ctx: CanvasRenderingContext2D,
  destX: number,
  destY: number,
  destW: number,
  destH: number,
  playback: PlaybackContext,
): void {
  const fontSize = Math.max(10, Math.min(12, destH * 0.018));
  const lineHeight = fontSize + 3;
  const padX = 8;
  const padY = 6;
  const margin = Math.max(6, destW * 0.012);

  const nameLabel = truncateLabel(playback.contentName, 48);
  const lines = [
    `Vídeo atual: ${nameLabel}`,
    `Tempo atual: ${playback.positionSec} de ${playback.durationSec}`,
    'Visualização Player: indisponível',
  ];

  ctx.save();
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'top';

  const textWidths = lines.map((line) => ctx.measureText(line).width);
  const boxW = Math.max(...textWidths) + padX * 2;
  const boxH = lines.length * lineHeight + padY * 2 - 3;
  const boxX = destX + destW - margin - boxW;
  const boxY = destY + destH - margin - boxH;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.72)';
  ctx.fillRect(boxX, boxY, boxW, boxH);

  ctx.fillStyle = '#f0f0f0';
  lines.forEach((line, i) => {
    ctx.fillText(line, boxX + boxW - padX, boxY + padY + i * lineHeight);
  });
  ctx.restore();
}

export interface LogicalSnapshotDrawResult {
  ok: boolean;
  frameUrl?: string;
  fromCache?: boolean;
  playbackContext?: PlaybackContext;
}

/**
 * Snapshot lógico em tela cheia: apaga o DOM capturado (html2canvas), centraliza
 * a mini — sem textos/UI do player; legenda fica no overlay HTML do admin.
 */
export async function composeLogicalSnapshotFullFrame(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
): Promise<LogicalSnapshotDrawResult> {
  const contentId = resolveVideoContentId(video);
  if (!contentId) {
    return { ok: false };
  }

  const playbackContext = resolvePlaybackContext(contentId, video);
  const frame = await getOrFetchFrameImage(
    contentId,
    playbackContext.positionSec,
  );
  if (!frame) {
    return { ok: false, playbackContext };
  }

  const canvasW = ctx.canvas.width;
  const canvasH = ctx.canvas.height;

  try {
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvasW, canvasH);

    drawCenteredThumbnailContain(ctx, frame.img, 0, 0, canvasW, canvasH);
    // Legenda fica no overlay HTML do admin (XX incrementa sem re-fetch).

    return {
      ok: true,
      frameUrl: frame.frameUrl,
      fromCache: frame.fromCache,
      playbackContext,
    };
  } catch (err) {
    console.warn('[captureLogicalFallback] composição full-frame falhou:', err);
    return { ok: false, playbackContext };
  }
}

/** @deprecated Use {@link composeLogicalSnapshotFullFrame} — mantido p/ testes. */
export async function drawLogicalSnapshotFallback(
  ctx: CanvasRenderingContext2D,
  destX: number,
  destY: number,
  destW: number,
  destH: number,
  video: HTMLVideoElement,
): Promise<LogicalSnapshotDrawResult> {
  void destX;
  void destY;
  void destW;
  void destH;
  return composeLogicalSnapshotFullFrame(ctx, video);
}
